/**
 * ============================================================================
 * useFilterOptions — store-aware hook over the pure selectFilterOptions.
 * ============================================================================
 *
 * Lives at the component layer (not features/) so imports keep pointing
 * DOWNWARD: components → features → types. The pure cascade logic is in
 * src/features/api/filterOptions.ts; this hook only wires the Redux store
 * (cached getMetrics responses + active filter/network state) into it.
 */

import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from 'src/app/store';
import { metricsApi } from 'src/features/api/metricsApi';
import { selectFilterOptions, FilterOption } from 'src/features/api/filterOptions';
import { GetMetricsOutput } from 'src/types/metricsApi.types';
import { STATIC_FILTER_OPTIONS } from 'src/config/filterConfig';

/**
 * Static options (Report Period, Metric Group, OTD threshold) + cascaded
 * data-driven dimension values from every fulfilled getMetrics cache entry,
 * narrowed by the active filters (never by a page-level network scope).
 */
export function useFilterOptions(): FilterOption[] {
  // Documented RTK Query APIs only (no reaching into internal state shape):
  // selectCachedArgsForQuery lists the args of every cached getMetrics entry;
  // endpoints.getMetrics.select(arg) resolves each entry's data.
  const responses = useAppSelector(
    (state) => {
      const args = metricsApi.util.selectCachedArgsForQuery(state, 'getMetrics');
      return args
        .map((arg) => metricsApi.endpoints.getMetrics.select(arg)(state).data)
        .filter((data): data is GetMetricsOutput => data !== undefined);
    },
    // New array identity per call — compare by content (entries' data refs
    // are stable between fetches, so this is cheap).
    shallowEqual,
  );
  const filterQuery = useAppSelector((state) => state.dashboard.filterQuery);

  // No page-level network scope (QuickSight parity: the demand-category
  // checkbox row shows/hides visuals, it does not cascade the filter
  // controls) — only the OTHER active tokens narrow each property's options.
  return useMemo(() => {
    return [
      ...STATIC_FILTER_OPTIONS,
      ...selectFilterOptions(responses, { query: filterQuery }),
    ];
  }, [responses, filterQuery]);
}
